// src/pages/en/base64Page_en.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../base64Page.css";

function Base64Page() {
  const [darkMode, setDarkMode] = useState(false);

  // CSR canonical URL
  const canonicalUrl = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.location.origin + window.location.pathname;
    }
    return null;
  }, []);

  // JSON-LD (SoftwareApplication)
  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Base64 Encoder/Decoder",
      description:
        "A TooLoves tool to encode text to Base64 or decode Base64 to UTF-8 text",
      applicationCategory: "UtilityApplication",
      operatingSystem: "Web",
      softwareVersion: "1.0.0",
      keywords:
        "Base64, Base64 Encoder, Base64 Decoder, encoding, decoding, text conversion, URL-safe, TooLoves, free",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl]);

  // Sync meta theme-color with dark/light mode
  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", darkMode ? "#111827" : "#ffffff");
    } else {
      const newMeta = document.createElement("meta");
      newMeta.name = "theme-color";
      newMeta.content = darkMode ? "#111827" : "#ffffff";
      document.head.appendChild(newMeta);
    }
  }, [darkMode]);

  // Hidden file input ref
  const fileInputRef = useRef(null);

  // Input ref
  const inputRef = useRef(null);

  // State
  const [mode, setMode] = useState("encode"); // encode | decode
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [decodedBytes, setDecodedBytes] = useState(null); // keep decode result
  const [urlSafe, setUrlSafe] = useState(false);
  const [error, setError] = useState("");

  // File button click -> trigger hidden input
  const onPickClick = () => {
    if (!fileInputRef.current) return;
    try {
      fileInputRef.current.value = "";
    } catch {}
    fileInputRef.current.click();
  };

  // Load text on file select
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setInputText(text);
    } catch (err) {
      setError("An error occurred while reading the file.");
    }
  };

  // Auto-resize textarea
  const autoResize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    autoResize(inputRef.current);
  }, [inputText]);

  // Base64 utils
  const bytesToBase64 = (bytes) => {
    let binary = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      const sub = bytes.subarray(i, i + chunk);
      binary += String.fromCharCode(...sub);
    }
    return btoa(binary);
  };

  const base64ToBytes = (b64) => {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  };

  const encodeUtf8ToBase64 = (text, { urlSafe }) => {
    const bytes = new TextEncoder().encode(text);
    let b64 = bytesToBase64(bytes);
    if (urlSafe) {
      b64 = b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
    }
    return b64;
  };

  const normalizeBase64Input = (raw, { urlSafe }) => {
    let s = raw || "";
    // Remove whitespace
    s = s.replace(/\s+/g, "");
    if (urlSafe) s = s.replace(/-/g, "+").replace(/_/g, "/");
    // padding
    const pad = s.length % 4;
    if (pad > 0) s = s + "=".repeat(4 - pad);
    return s;
  };

  const decodeBase64ToUtf8 = (raw, { urlSafe }) => {
    const normalized = normalizeBase64Input(raw, { urlSafe });
    const bytes = base64ToBytes(normalized);
    const text = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    return { text, bytes };
  };

  // Readability formatter: 76-char wrap + indent (encode mode only)
  const wrapAndIndent = (str, width = 76, indent = "  ") => {
    if (!str) return "";
    const out = [];
    for (let i = 0; i < str.length; i += width) {
      out.push(indent + str.slice(i, i + width));
    }
    return out.join("\n");
  };

  const getDisplayOutput = () => {
    if (!outputText) return "";
    // Only apply 76-char wrap + indent for Base64 encoding output
    if (mode === "encode") {
      return wrapAndIndent(outputText, 76, "  ");
    }
    return outputText;
  };

  // Transform
  const transform = () => {
    setError("");
    try {
      if (mode === "encode") {
        const b64 = encodeUtf8ToBase64(inputText, { urlSafe });
        setOutputText(b64);
        setDecodedBytes(null);
      } else {
        const { text, bytes } = decodeBase64ToUtf8(inputText, { urlSafe });
        setOutputText(text);
        setDecodedBytes(bytes);
      }
    } catch (err) {
      setOutputText("");
      setDecodedBytes(null);
      setError("Invalid input or an error occurred during conversion.");
    }
  };

  useEffect(() => {
    transform();
  }, [inputText, mode, urlSafe]); // eslint-disable-line react-hooks/exhaustive-deps

  // Copy to clipboard (based on display format)
  const handleCopy = async () => {
    try {
      const data = getDisplayOutput() || "";
      await navigator.clipboard.writeText(data);
    } catch (err) {
      setError("Failed to copy to clipboard.");
    }
  };

  // Download as TXT (based on display format)
  const handleDownload = () => {
    try {
      const data = getDisplayOutput() || "";
      const blob = new Blob([data], { type: "text/plain;charset=utf-8" });
      const filename = mode === "encode" ? "base64.txt" : "decoded.txt";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError("An error occurred during download.");
    }
  };

  const handleReset = () => {
    setInputText("");
    setOutputText("");
    setDecodedBytes(null);
    setMode("encode");
    setUrlSafe(false);
    setError("");
    requestAnimationFrame(() => {
      autoResize(inputRef.current);
    });
  };

  const handleSwap = () => {
    if (!outputText) return;
    if (mode === "encode") {
      setInputText(outputText);
      setMode("decode");
    } else {
      setInputText(outputText);
      setMode("encode");
    }
  };

  const isOutputEmpty = !outputText || outputText.trim() === "";
  const displayOutput = getDisplayOutput();

  return (
    <div className={`App ${darkMode ? "dark" : "light"}`}>
      <Helmet htmlAttributes={{ lang: "en" }}>
        <title>Base64 Encoder/Decoder | TooLoves</title>
        <meta
          name="description"
          content="Encode text to Base64 or decode Base64 strings to UTF-8 text with this online tool"
        />
        <meta
          name="keywords"
          content="Base64, encoding, decoding, URL-safe, text conversion, TooLoves, free"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* Initial theme-color; synchronized at runtime via useEffect */}
        <meta name="theme-color" content={darkMode ? "#111827" : "#ffffff"} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="Base64 Encoder/Decoder | TooLoves" />
        <meta
          property="og:description"
          content="Easily convert between text and Base64"
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-base64.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Base64 Encoder/Decoder | TooLoves" />
        <meta
          name="twitter:description"
          content="Easily convert between text and Base64"
        />
        <meta name="twitter:image" content="/og-base64.png" />

        {/* Canonical */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      {/* Top app bar */}
      <header className="app-bar">
        <div className="app-bar__inner">
          <Link to="/en" className="brand-link" aria-label="Go to TooLoves home">
            TooLoves
          </Link>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="mode-toggle" onClick={() => setDarkMode(!darkMode)}>
              {darkMode ? "Light mode" : "Dark mode"}
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main
        className="container"
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          paddingLeft: 16,
          paddingRight: 16,
        }}
      >
        <section className="generator-card">
          <h2 className="card-title">Base64 Encoder/Decoder</h2>
          <p className="card-subtitle">
            Encode text to Base64 or decode Base64 to UTF-8.
          </p>

          <form className="form" onSubmit={(e) => e.preventDefault()}>
            {/* File upload (hidden input + button) */}
            <div className="input-row">
              <input
                ref={fileInputRef}
                id="b64-file-input"
                type="file"
                accept="text/plain,.txt"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <button
                type="button"
                onClick={onPickClick}
                aria-label="Choose a text file"
                className="btn btn-primary"
              >
                Choose file
              </button>
            </div>

            {/* Input */}
            <div className="input-row" style={{ marginTop: 8 }}>
              <div className="io-wrap" style={{ width: "100%" }}>
                <textarea
                  ref={inputRef}
                  className="text-input text-inputtextarea mono"
                  placeholder={
                    mode === "encode"
                      ? "Enter text here or choose a file."
                      : "Paste a Base64 string here."
                  }
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  rows={4}
                  style={{
                    width: "100%",
                    display: "block",
                    maxWidth: "1200px",
                    margin: "0 auto",
                  }}
                  spellCheck={false}
                />
              </div>
            </div>

            {/* Options */}
            <div className="options-row" style={{ gap: 16 }}>
              <div className="field">
                <label className="form-label">Mode</label>
                <select value={mode} onChange={(e) => setMode(e.target.value)}>
                  <option value="encode">Encode (Text → Base64)</option>
                  <option value="decode">Decode (Base64 → Text)</option>
                </select>
              </div>

              <div className="field switch">
                <label className="form-label">URL-safe</label>
                <input
                  type="checkbox"
                  checked={urlSafe}
                  onChange={(e) => setUrlSafe(e.target.checked)}
                />
              </div>
            </div>
          </form>

          {/* Output */}
          <div className="metadata-section" style={{ paddingTop: 8 }}>
            <div className="block">
              <div className="label-row">
                <label htmlFor="b64-output" className="label-strong">
                  Output
                </label>
              </div>

              <div
                id="b64-output"
                role="textbox"
                aria-readonly="true"
                tabIndex={0}
                className="mono-box"
                style={{ whiteSpace: "pre-wrap" }}
              >
                {displayOutput ||
                  (mode === "encode"
                    ? "Encoded result will appear here."
                    : "Decoded result will appear here.")}
              </div>

              {error && (
                <p className="hint" role="alert" style={{ color: "#ef4444", marginTop: 8 }}>
                  {error}
                </p>
              )}
            </div>

            {/* Actions: Copy/Download + Swap/Reset */}
            <div className="actions">
              <button
                className="btn-primary"
                onClick={handleCopy}
                type="button"
                aria-label="Copy output"
                disabled={isOutputEmpty}
              >
                Copy
              </button>
              <button
                className="btn-primary"
                onClick={handleDownload}
                type="button"
                aria-label="Download output as txt"
                disabled={isOutputEmpty}
              >
                Download TXT
              </button>
              <button className="btn-secondary" onClick={handleSwap} type="button">
                Swap
              </button>
              <button className="btn-secondary" onClick={handleReset} type="button">
                Reset
              </button>
            </div>

            {/* Byte length hint on decode */}
            {mode === "decode" && decodedBytes && (
              <p className="hint" style={{ marginTop: 8 }}>
                Byte length: {decodedBytes.byteLength.toLocaleString()} bytes
              </p>
            )}
          </div>
        </section>
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. All rights reserved.</span>
        <nav className="footer-links" aria-label="Legal links">
          <Link to="/en/terms" className="footer-link">
            Terms of Service
          </Link>
          <span className="footer-sep" aria-hidden="true">
            ·
          </span>
          <Link to="/en/privacy" className="footer-link">
            Privacy Policy
          </Link>
        </nav>
      </footer>
    </div>
  );
}

export default Base64Page;